import React, { createContext, useContext, useEffect, useState } from 'react';
import { getApiUrl } from '../../CompanyAdmin/utils/api';
import { forgetTeamLogin } from '../../HireWithUs/Context/CompanyAccessContext';

const RecruiterAuthContext = createContext(null);

export function useRecruiterAuth() {
  const ctx = useContext(RecruiterAuthContext);
  if (!ctx) throw new Error('useRecruiterAuth must be used within RecruiterAuthProvider');
  return ctx;
}

export function RecruiterAuthProvider({ children }) {
  const [recruiter, setRecruiter] = useState(null); // { id, email, company_name }
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  const hydrateFromStorage = () => {
    const id = sessionStorage.getItem('recruiter_id') || localStorage.getItem('recruiter_id');
    const email = sessionStorage.getItem('recruiter_email') || localStorage.getItem('recruiter_email');
    const company = sessionStorage.getItem('recruiter_company_name') || localStorage.getItem('recruiter_company_name') || '';
    if (id && email) {
      setRecruiter({ id, email, company_name: company });
      setIsAuthenticated(true);
    } else {
      setRecruiter(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    hydrateFromStorage();
    setChecking(false);
    // Optionally you could call an endpoint to validate session here.
  }, []);

  const login = async ({ email, password }) => {
    // call backend /api/recruiter-login
    const resp = await fetch(getApiUrl('/api/recruiter-login'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data.message || `Login failed (${resp.status})`);
    }

    // Expected data: { recruiter_id, recruiter_email, company_name, needs_profile }
    const recruiterObj = {
      id: data.recruiter_id,
      email: data.recruiter_email,
      company_name: data.company_name || ''
    };

    // persist
    sessionStorage.setItem('recruiter_id', recruiterObj.id);
    sessionStorage.setItem('recruiter_email', recruiterObj.email);
    sessionStorage.setItem('recruiter_company_name', recruiterObj.company_name);

    localStorage.setItem('recruiter_id', recruiterObj.id);
    localStorage.setItem('recruiter_email', recruiterObj.email);
    if (recruiterObj.company_name) localStorage.setItem('recruiter_company_name', recruiterObj.company_name);

    forgetTeamLogin();

    // Signed token — the unforgeable identity the backend authorizes on (attached
    // to API calls by src/utils/apiAuthInterceptor). Works cross-domain. T0-5.
    if (data.recruiter_token) {
      sessionStorage.setItem('recruiter_token', data.recruiter_token);
      localStorage.setItem('recruiter_token', data.recruiter_token);
    }

    setRecruiter(recruiterObj);
    setIsAuthenticated(true);

    return { needs_profile: !!data.needs_profile };
  };

  const completeProfile = async ({ company_name, new_password }) => {
    // ensure we have recruiter info in memory/storage
    const recruiter_email = recruiter?.email || sessionStorage.getItem('recruiter_email') || localStorage.getItem('recruiter_email');
    const recruiter_id = recruiter?.id || sessionStorage.getItem('recruiter_id') || localStorage.getItem('recruiter_id');

    if (!recruiter_email) {
      throw new Error('No recruiter email found in session/storage. Please login again.');
    }

    const payload = {
      recruiter_email,
      recruiter_id,      // optional, backend may ignore if not needed
      company_name,
      new_password
    };

    const resp = await fetch(getApiUrl('/api/recruiter/complete_profile'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data.error || data.message || `Update failed (${resp.status})`);
    }

    // update local state + storage
    const updated = { ...(recruiter || {}), company_name };
    setRecruiter(updated);
    sessionStorage.setItem('recruiter_company_name', company_name);
    localStorage.setItem('recruiter_company_name', company_name);

    return data;
  };

  const logout = async () => {
    // Enterprise-grade: nuke server session first, then wipe ALL identity
    // keys (not just recruiter_*) to prevent cross-role contamination on
    // subsequent logins in the same browser.
    try {
      const { getApiUrl } = await import('../../config/api');
      await Promise.allSettled([
        fetch(getApiUrl('/api/recruiter/logout'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(getApiUrl('/api/logout'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
      ]);
    } catch (_) {}

    const IDENTITY_STORAGE_KEYS = [
      'employer_email', 'employer_password', 'employer_token',
      'company_email', 'company_token', 'company_name', 'companyAccountId',
      'admin_email', 'admin_id', 'admin_full_name', 'admin_token',
      'admin_role', 'is_super_admin',
      'recruiter_id', 'recruiter_email', 'recruiter_company_name', 'recruiter_token',
      'organizer_email', 'organizer_token',
      'actor_email', 'user_email', 'user_token',
    ];
    for (const key of IDENTITY_STORAGE_KEYS) {
      try { localStorage.removeItem(key); } catch (_) {}
      try { sessionStorage.removeItem(key); } catch (_) {}
    }

    setRecruiter(null);
    setIsAuthenticated(false);
  };

  return (
    <RecruiterAuthContext.Provider value={{
      recruiter,
      isAuthenticated,
      checking,
      login,
      logout,
      completeProfile
    }}>
      {children}
    </RecruiterAuthContext.Provider>
  );
}
